import { Box } from '@mui/material';
import gsap from 'gsap';
import { createArray, getTexture, getTextures } from 'helpers';
import _ from 'lodash';
import * as PIXI from 'pixi.js';
import React, { createRef, RefObject } from 'react';

export default class LandingAnts extends React.Component {
    app: PIXI.Application;
    cake: PIXI.Sprite;
    colony: PIXI.Sprite;
    containerRef: RefObject<HTMLDivElement | null>;
    startTimeout: NodeJS.Timeout;

    /** Whether the async pixi v8 Application.init() has completed */
    initialised = false;
    /** Whether the component unmounted before init completed */
    unmounted = false;

    tweens: gsap.core.Tween[] = [];

    constructor(props: any) {
        super(props);

        this.containerRef = createRef();
    }

    componentDidMount() {
        gsap.globalTimeline.resume();

        // Create app (pixi v8 initialises asynchronously)
        this.app = new PIXI.Application();

        // Add listeners
        window.addEventListener('resize', this.resize);

        // Initialize
        void this.setup();
    }

    componentDidUpdate() {
        this.mount();
    }

    componentWillUnmount() {
        this.unmounted = true;

        // Kill animations
        clearTimeout(this.startTimeout);
        this.tweens.forEach(tween => tween.kill());

        // Remove app
        if (this.app && this.initialised) {
            this.app.canvas.parentElement?.removeChild(this.app.canvas);
            this.app.destroy(true, {
                children: true,
            });
        }

        // Remove listeners
        window.removeEventListener('resize', this.resize);
    }

    setup = async () => {
        await this.app.init({
            antialias: true,
            backgroundAlpha: 0,
        });

        // Bail if the component unmounted while init was in flight
        if (this.unmounted) {
            this.app.destroy(true, { children: true });
            return;
        }
        this.initialised = true;

        this.mount();
        await this.init();
    };

    mount = () => {
        // Add view
        const { current } = this.containerRef;
        if (current && this.initialised) {
            current.appendChild(this.app.canvas);
            this.app.resizeTo = current;
        }
    };

    async init() {
        let antTextures: PIXI.Texture[];
        let cakeTexture: PIXI.Texture;
        let colonyTexture: PIXI.Texture;

        try {
            const antTexturePaths = createArray(12, i => `ant/worker/ant-worker-moving-${i + 1}.png`);
            antTextures = await getTextures(antTexturePaths);
            cakeTexture = await getTexture('food/food-cake.png');
            colonyTexture = await getTexture('nature/nature-antColony.png');
        } catch (e) {
            console.warn('Failed to load landing textures:', e);
            return;
        }

        // Add cake and colony
        this.colony = this.addSprite(colonyTexture, 0.2, 0);
        this.cake = this.addSprite(cakeTexture, 0.3, 0.1);
        this.resize();

        // Add ants
        for (let i = 0; i < 3; i++) {
            // Add ant
            const ant = new PIXI.AnimatedSprite(antTextures);
            this.app.stage.addChild(ant);
            ant.anchor.set(0.5);
            ant.position.copyFrom(this.colony.position);
            ant.rotation = _.random(0, Math.PI * 2);
            ant.scale.set(0);

            // Animate in
            const tween = gsap.to(ant.scale, {
                delay: 2 * i + 0.5,
                duration: 0.3,
                x: 0.7,
                y: 0.7,
                onComplete: () => {
                    this.moveAntToCake(ant);
                },
            });
            this.addTween(tween);
        }
    }

    resize = () => {
        if (!this.initialised) {
            return;
        }
        const { clientHeight, clientWidth } = this.app.canvas;

        // Update cake
        if (this.cake) {
            this.cake.x = 50;
            this.cake.y = clientHeight * 0.5;
        }

        // Update colony
        if (this.colony) {
            this.colony.x = clientWidth - 60;
            this.colony.y = clientHeight * 0.5;
        }
    };

    addSprite = (texture: PIXI.Texture, scale: number, delay: number) => {
        // Add sprite
        const sprite = new PIXI.Sprite(texture);
        this.app.stage.addChild(sprite);
        sprite.anchor.set(0.5);
        sprite.scale.set(0);

        // Animate in
        const tween = gsap.to(sprite.scale, {
            delay,
            duration: 0.3,
            x: scale,
            y: scale,
        });
        this.addTween(tween);

        return sprite;
    };

    moveAntToCake = (ant: PIXI.AnimatedSprite) => {
        const deviation = _.random(0.2, 0.5) * (this.colony.x - this.cake.x);
        const offset = _.random(-30, 30);
        const path = [
            {
                x: this.cake.x + deviation,
                y: this.cake.y + _.random(1.1, 1.5) * offset,
            },
            {
                x: this.cake.x + _.random(20, 40),
                y: this.cake.y + offset,
            },
        ];

        this.moveAnt(ant, path, this.moveAntToColony);
    };

    moveAntToColony = (ant: PIXI.AnimatedSprite) => {
        const deviation = _.random(0.5, 0.8) * (this.colony.x - this.cake.x);
        const offset = _.random(-40, 40);
        const path = [
            {
                x: this.cake.x + deviation,
                y: this.cake.y + _.random(1.1, 1.5) * offset,
            },
            {
                x: this.colony.x + _.random(-40, -20),
                y: this.colony.y + offset,
            },
        ];
        this.moveAnt(ant, path, this.moveAntToCake);
    };

    moveAnt = (ant: PIXI.AnimatedSprite, path: LandingAntsPath, callback: Function) => {
        // Get distance
        const target = path[path.length - 1];
        const dX = Math.abs(target.x - ant.x);
        const dY = Math.abs(target.y - ant.y);
        const distance = Math.sqrt(dX * dX + dY * dY);

        // Animate
        const tween = gsap.to(ant, {
            delay: _.random(0.5, 1),
            duration: distance / _.random(180, 220),
            ease: 'linear',
            motionPath: {
                autoRotate: Math.PI / 2,
                curviness: 1,
                path,
                useRadians: true,
            },
            onComplete: () => {
                ant.stop();
                callback(ant);
            },
            onStart: () => {
                ant.play();
            },
        });
        this.addTween(tween);
    };

    addTween = (tween: gsap.core.Tween) => {
        this.tweens.push(tween);

        // Clean up completed tweens
        let i = 0;
        while (i < this.tweens.length) {
            if (this.tweens[i].ratio === 1) {
                this.tweens.splice(i, 1);
            } else {
                i++;
            }
        }
    };

    render() {
        return (
            <Box
                bottom={-20}
                height={200}
                overflow="hidden"
                position="absolute"
                ref={this.containerRef}
                right={0}
                width="calc(100% - 320px)"
                zIndex={1}
            />
        );
    }
}
